import { NET_INTERP } from './constants';

/** Stable IDs for `RemoteVehicleInterpolator` (multiplayer). */
export const REMOTE_ENTITY = {
  COP: 'remote_cop',
  DRIVER: 'remote_driver',
} as const;

/**
 * One authoritative (or predicted) vehicle frame in world space.
 * `t` must be comparable across frames (server time or synced client clock, ms).
 */
export type VehicleSnapshot = {
  t: number;
  x: number;
  z: number;
  vx: number;
  vz: number;
  /** radians — Three.js car convention (+Z forward at 0). */
  heading: number;
};

function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u;
}

/** Shortest-path angle blend. */
function lerpHeading(a: number, b: number, u: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  let out = a + d * u;
  while (out > Math.PI) out -= Math.PI * 2;
  while (out < -Math.PI) out += Math.PI * 2;
  return out;
}

function extrapolateFrom(
  s: VehicleSnapshot,
  targetT: number,
): VehicleSnapshot {
  const dt = (targetT - s.t) / 1000;
  const clamped = Math.max(
    -NET_INTERP.MAX_BACK_EXTRAPOLATE_S,
    Math.min(dt, NET_INTERP.MAX_EXTRAPOLATE_S),
  );
  return {
    t: targetT,
    x: s.x + s.vx * clamped,
    z: s.z + s.vz * clamped,
    vx: s.vx,
    vz: s.vz,
    heading: s.heading,
  };
}

function pruneBuffer(buf: VehicleSnapshot[], tCut: number): void {
  while (buf.length > 0 && buf[0].t < tCut) {
    buf.shift();
  }
}

/**
 * Buffers timestamped snapshots per remote entity and returns smooth display poses.
 * Wire `pushFrame` from WebSocket `state` messages; call `getDisplayState` each render frame.
 */
export class RemoteVehicleInterpolator {
  private readonly buffers = new Map<string, VehicleSnapshot[]>();

  pushFrame(id: string, snap: VehicleSnapshot): void {
    let buf = this.buffers.get(id);
    if (!buf) {
      buf = [];
      this.buffers.set(id, buf);
    }
    buf.push(snap);
    buf.sort((a, b) => a.t - b.t);
    while (buf.length > NET_INTERP.MAX_SNAPSHOTS) {
      buf.shift();
    }
  }

  removeEntity(id: string): void {
    this.buffers.delete(id);
  }

  clear(): void {
    this.buffers.clear();
  }

  /**
   * @param nowMs — current time in same space as snapshot `t` (e.g. `performance.now()` after offset sync).
   */
  getDisplayState(id: string, nowMs: number): VehicleSnapshot | null {
    const buf = this.buffers.get(id);
    if (!buf || buf.length === 0) return null;

    const targetT = nowMs - NET_INTERP.INTERP_DELAY_MS;
    pruneBuffer(buf, targetT - NET_INTERP.MAX_BUFFER_MS);

    if (buf.length === 0) return null;

    if (buf.length === 1) {
      return extrapolateFrom(buf[0], targetT);
    }

    if (targetT <= buf[0].t) {
      return extrapolateFrom(buf[0], targetT);
    }

    const last = buf[buf.length - 1];
    if (targetT >= last.t) {
      return extrapolateFrom(last, targetT);
    }

    let lo = 0;
    for (let i = 0; i < buf.length - 1; i++) {
      if (buf[i].t <= targetT) lo = i;
    }
    const a = buf[lo];
    const b = buf[lo + 1];
    if (!b || a.t === b.t) {
      return extrapolateFrom(a, targetT);
    }
    const u = (targetT - a.t) / (b.t - a.t);
    return {
      t: targetT,
      x: lerp(a.x, b.x, u),
      z: lerp(a.z, b.z, u),
      vx: lerp(a.vx, b.vx, u),
      vz: lerp(a.vz, b.vz, u),
      heading: lerpHeading(a.heading, b.heading, u),
    };
  }
}

/** Dev-only sanity check — two samples, interpolation lands between them. */
export function runEntityInterpolationSelfTest(): void {
  const interp = new RemoteVehicleInterpolator();
  interp.pushFrame('car1', {
    t: 0,
    x: 0,
    z: 0,
    vx: 0,
    vz: 0,
    heading: 0,
  });
  interp.pushFrame('car1', {
    t: 1000,
    x: 10,
    z: 0,
    vx: 0,
    vz: 0,
    heading: 0,
  });
  const nowMs = 600 + NET_INTERP.INTERP_DELAY_MS;
  const out = interp.getDisplayState('car1', nowMs);
  if (!out) {
    throw new Error('entityInterpolation self-test: null');
  }
  const expectedX = 6;
  if (Math.abs(out.x - expectedX) > 0.001) {
    throw new Error(
      `entityInterpolation self-test: expected x≈${expectedX}, got ${out.x}`,
    );
  }
}
